// ✅ CORRECT: useEffect comes from React, not react-router-dom
import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";

// ✅ Import Keep-Alive Service
import keepAliveService from "./services/keepAlive";

// Add the UserDataProvider import
import { UserDataProvider } from "./context/UserDataContext";

// ✅ Import both AdminRoute and UserRoute
import AdminRoute from "./components/AdminRoute";
import UserRoute from "./components/UserRoute";

// Layout Components
import PublicLayout from "./components/Layout/PublicLayout";
import DashboardLayout from "./components/Layout/DashboardLayout";

// Pages
import Home from "./pages/HomePage/HomePage";
import Login from "./pages/HomePage/Login";
import Register from "./pages/HomePage/Register";
import Dashboard from "./pages/Dashboard/Dashboard";
import AdminPortal from "./pages/Admin/AdminPortal";

// MetaTrader Components
import MetaTraderLogin from "./pages/MetaTrader/MetaTraderLogin";
import MetaTraderPlatform from "./pages/MetaTrader/MetaTraderPlatform";

// ✅ Keep-Alive Status Component (silent for users)
import KeepAliveStatus from "./components/keepAliveStatus/keepAliveStatus";

export default function App() {
  const { user, loading } = useAuth();

  // ✅ Initialize keep-alive system on app startup
  useEffect(() => {
    console.log('🚀 App: Initializing StoneForex application...');
    
    // ✅ Cleanup keep-alive on app unmount
    return () => {
      console.log('👋 App: Shutting down keep-alive service');
      keepAliveService.stop();
    };
  }, []);

  // ✅ Show loading spinner while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f4f6',
          borderTop: '4px solid #1fa84f',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div>Loading StoneForex...</div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <UserDataProvider>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route 
            path="/login" 
            element={user ? (
              // ✅ Proper role-based redirect after login check
              user.isAdmin || user.role === 'admin' ? 
                <Navigate to="/admin" replace /> : 
                <Navigate to="/dashboard" replace />
            ) : <Login />} 
          />
          <Route 
            path="/create-account" 
            element={user ? (
              user.isAdmin || user.role === 'admin' ? 
                <Navigate to="/admin" replace /> : 
                <Navigate to="/dashboard" replace />
            ) : <Register />} 
          />
        </Route>

        {/* ✅ Protected Dashboard Routes (Users Only) */}
        <Route element={<DashboardLayout />}>
          <Route path="/dashboard/*" element={
            <UserRoute>
              <Dashboard />
            </UserRoute>
          } />
        </Route>

        {/* MetaTrader Routes - Standalone (no layout wrapper) */}
        <Route path="/metatrader-login" element={user ? <MetaTraderLogin /> : <Navigate to="/login" />} />
        <Route path="/metatrader-platform" element={user ? <MetaTraderPlatform /> : <Navigate to="/login" />} />

        {/* ✅ Single Admin Route (No Duplicates) */}
        <Route path="/admin/*" element={
          <AdminRoute>
            <AdminPortal />
          </AdminRoute>
        } />
        
        {/* ✅ BACKWARD COMPATIBILITY: Redirect old /admin-portal/* to /admin/* */}
        <Route path="/admin-portal/*" element={<Navigate to="/admin" replace />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* ✅ Keep-Alive Status - Only visible to developers/admins, INVISIBLE to regular users */}
      <KeepAliveStatus />
    </UserDataProvider>
  );
}
